import { useEffect, useMemo, useState } from "react";
import { Button, Modal, Tag } from "antd";
import { ExclamationCircleFilled, PaperClipOutlined } from "@ant-design/icons";
import moment from "moment";
import config from "../../config";
import "./announcement-modal.css";

const getAttachments = (item: any) => {
  const attachments = Array.isArray(item.attachments) ? item.attachments : [];
  return attachments.length > 0
    ? attachments
    : item.attachmentName
      ? [{ name: item.attachmentName, originalName: item.attachmentOriginalName || item.attachmentName }]
      : [];
};

const priority = (item: any) => Number(item?.priority || item?.importance || 0);

const orderNotifications = (items: any[]) =>
  [...items].sort((first, second) => {
    const priorityDifference = priority(second) - priority(first);
    if (priorityDifference !== 0) return priorityDifference;
    return new Date(second.createdAt || second.startDate || 0).getTime() - new Date(first.createdAt || first.startDate || 0).getTime();
  });

const getAccent = (item: any) => {
  const text = `${item?.title || ""} ${item?.content || ""}`.toLowerCase();
  if (text.includes("яаралтай")) return { color: "#cf1322", background: "#fff1f0", label: "Яаралтай" };
  if (text.includes("амжилт") || text.includes("баталгааж")) return { color: "#389e0d", background: "#f6ffed", label: "Мэдээлэл" };
  if (text.includes("мэдээлэл")) return { color: "#2f6fa7", background: "#f0f7ff", label: "Мэдээлэл" };
  return { color: "#d48806", background: "#fffbe6", label: "Анхааруулга" };
};

interface Props {
  open: boolean;
  announcements: any[];
  onClose: () => void;
}

const AnnouncementModal = ({ open, announcements, onClose }: Props) => {
  const ordered = useMemo(() => orderNotifications(announcements), [announcements]);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (open) setIndex(0);
  }, [open, announcements]);

  const current = ordered[index];
  const isLast = index >= ordered.length - 1;
  const showNext = () => {
    if (isLast) onClose();
    else setIndex((currentIndex) => currentIndex + 1);
  };

  if (!current) return null;

  const accent = getAccent(current);
  const attachments = getAttachments(current);

  return (
    <Modal
      open={open}
      onCancel={showNext}
      maskClosable={false}
      keyboard
      width={680}
      style={{ maxWidth: "calc(100vw - 24px)", top: 24 }}
      title={<div className="announcement-modal-title"><span>Мэдэгдэл</span><span className="announcement-modal-progress">{index + 1} / {ordered.length}</span></div>}
      footer={<Button type="primary" size="large" onClick={showNext} className="announcement-modal-next">{isLast ? "Ойлголоо, хаах" : "Ойлголоо, дараагийнх"}</Button>}
      bodyStyle={{ maxHeight: "68vh", overflowY: "auto", padding: "24px" }}
      destroyOnClose
    >
      <section key={current.id} className="announcement-sequential-content">
        <div className="announcement-type" style={{ color: accent.color }}>
          <ExclamationCircleFilled />
          <Tag color={accent.color} style={{ marginLeft: 8 }}>{accent.label}</Tag>
        </div>
        <h2 className="announcement-title">{current.title}</h2>
        <div className="announcement-full-content" style={{ background: accent.background, borderColor: `${accent.color}44` }}>
          {current.content}
        </div>
        {attachments.length > 0 && <div className="announcement-attachments">
          {attachments.map((file: any) => <div key={file.name}>
            <PaperClipOutlined style={{ color: accent.color, marginRight: 7 }} />
            <a href={`${config.get("apiUrl")}/tmp/${file.name}`} target="_blank" rel="noreferrer">{file.originalName || file.name}</a>
          </div>)}
        </div>}
        <div className="announcement-meta">
          {current.organizationName || "-"}<span>•</span>{current.createdAt ? moment(current.createdAt).format("YYYY.MM.DD HH:mm") : "-"}
        </div>
      </section>
    </Modal>
  );
};

export default AnnouncementModal;
